<template>
  <div class="py-4 container-fluid">
    <div class="row mb-3">
      <div class="col-12 d-flex flex-wrap justify-content-between align-items-center">
        <div>
          <h5 class="mb-0">{{ $t('reports.toolDowntimeTitle') }}</h5>
          <p class="mb-0 text-sm text-secondary">{{ $t('reports.toolDowntimeSubtitle') }}</p>
        </div>
        <material-button variant="outline" color="dark" size="sm" :disabled="!rows.length" @click="onExport">
          <i class="material-icons text-sm me-1">download</i>{{ $t('reports.exportCsv') }}
        </material-button>
      </div>
    </div>

    <div class="card mb-3">
      <div class="card-body d-flex flex-wrap gap-3 align-items-end">
        <div>
          <label class="text-xs text-secondary d-block mb-1">{{ $t('common.from') }}</label>
          <el-date-picker v-model="fromDate" type="date" value-format="YYYY-MM-DD" format="DD/MM/YYYY" :clearable="false" @change="reload" />
        </div>
        <div>
          <label class="text-xs text-secondary d-block mb-1">{{ $t('common.to') }}</label>
          <el-date-picker v-model="toDate" type="date" value-format="YYYY-MM-DD" format="DD/MM/YYYY" :clearable="false" @change="reload" />
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-secondary" role="status"></div>
        </div>
        <template v-else>
          <el-table :data="rows" stripe>
            <el-table-column prop="toolName" :label="$t('reports.tool')" min-width="160" />
            <el-table-column prop="toolType" :label="$t('reports.toolType')" min-width="120" />
            <el-table-column prop="downtimeDays" :label="$t('reports.downtimeDays')" align="center" min-width="120" />
            <el-table-column prop="breakdownCount" :label="$t('reports.breakdowns')" align="center" min-width="110" />
            <el-table-column :label="$t('reports.meanDaysBetween')" align="center" min-width="130">
              <template #default="{ row }">{{ row.meanDaysBetweenBreakdowns ?? '–' }}</template>
            </el-table-column>
            <el-table-column :label="$t('reports.downtimeCost')" align="right" min-width="130">
              <template #default="{ row }">{{ money(row.downtimeCost) }}</template>
            </el-table-column>
            <el-table-column :label="$t('reports.status')" align="center" min-width="110">
              <template #default="{ row }">
                <span class="badge" :class="row.currentlyDown ? 'bg-gradient-danger' : 'bg-gradient-success'">
                  {{ row.currentlyDown ? $t('reports.down') : $t('reports.up') }}
                </span>
              </template>
            </el-table-column>
          </el-table>
          <div class="d-flex justify-content-end mt-3">
            <el-pagination v-model:current-page="currentPage" :page-size="pageSize" :total="totalItems" layout="prev, pager, next" @current-change="fetchData" />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../../services/api";
import MaterialButton from "@/components/MaterialButton.vue";
import { exportCsv } from "../../utils/csvExport";

export default {
  name: "ToolDowntimeReport",
  components: { MaterialButton },
  data() {
    const today = new Date();
    const from = new Date();
    from.setMonth(today.getMonth() - 3);
    return {
      fromDate: this.toIso(from),
      toDate: this.toIso(today),
      rows: [],
      totalItems: 0,
      currentPage: 1,
      pageSize: 20,
      loading: true,
    };
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    toIso(d) {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    },
    money(v) {
      return `${new Intl.NumberFormat("is-IS").format(Math.round(v || 0))} kr.`;
    },
    reload() {
      this.currentPage = 1;
      this.fetchData();
    },
    async fetchData() {
      this.loading = true;
      try {
        const response = await api.get("/Analytics/Tools/Downtime", {
          params: { fromDate: this.fromDate, toDate: this.toDate, currentPage: this.currentPage, pageSize: this.pageSize },
        });
        this.rows = response.data.result || [];
        this.totalItems = response.data.totalItems || 0;
      } catch (e) {
        console.error("ToolDowntimeReport fetchData error:", e); // eslint-disable-line no-console
        this.$toast.error(this.$t("common.unableToRetrieveData"), { timeout: 0 });
      } finally {
        this.loading = false;
      }
    },
    onExport() {
      const columns = [
        { key: "toolName", label: this.$t("reports.tool") },
        { key: "toolType", label: this.$t("reports.toolType") },
        { key: "downtimeDays", label: this.$t("reports.downtimeDays") },
        { key: "breakdownCount", label: this.$t("reports.breakdowns") },
        { key: "meanDaysBetweenBreakdowns", label: this.$t("reports.meanDaysBetween") },
        { key: "downtimeCost", label: this.$t("reports.downtimeCost") },
        { key: "currentlyDown", label: this.$t("reports.status") },
      ];
      exportCsv(`tool-downtime-${this.fromDate}_${this.toDate}`, columns, this.rows);
    },
  },
};
</script>
